// @flow

import { LOCATION_CHANGE } from "connected-react-router";
import { startRequest, finishRequest } from "@store/utils";
import {
  ExportAbsenceMonitoringReport,
  ExportAbsenceMonitoringReportSuccess,
  ExportAbsenceMonitoringReportError,
  GetReportingAbsenceMonitoringDefaultReports,
  GetReportingAbsenceMonitoringDefaultReportsSuccess,
  GetReportingAbsenceMonitoringDefaultReportsError,
  GetReportingAbsenceMonitoringMyReports,
  GetReportingAbsenceMonitoringMyReportsSuccess,
  GetReportingAbsenceMonitoringMyReportsError,
  GetReportingAbsenceMonitoringReport,
  GetReportingAbsenceMonitoringReportSuccess,
  GetReportingAbsenceMonitoringReportError,
  GetReportingAbsenceMonitoringChartsSuccess,
  GetReportingAbsenceMonitoringCharts,
  GetReportingAbsenceMonitoringChartsError,
  GetAbsenceMonitoringFiltersSchema,
  GetAbsenceMonitoringFiltersSchemaSuccess,
  GetAbsenceMonitoringFiltersSchemaError,
  SetAbsenceMonitoringReportToSave,
  SetAbsenceMonitoringReportViewState,
} from "@modules/reporting/constants/actions";
import type { AbsenceMonitoringState, AbsenceMonitoringReportViewState } from "./types";
import { cloneObject } from "@utils/object";

export const initialViewState: AbsenceMonitoringReportViewState = {
  filtersExpanded: false,
  chartReviewMeetingStatusExpanded: false,
  chartEmployeesPerLevelExpanded: true,
  reportTableExpanded: true,
};

export const initialState: AbsenceMonitoringState = {
  error: null,
  requestsInProgress: 0,
  reportGenerateInProgress: 0,
  defaultReports: [],
  mySavedReports: [],
  currentReport: null,
  currentCharts: null,
  reportData: {
    content: [],
    pagination: {
      sort: { orders: [] },
      pageNumber: 0,
      pageSize: 0,
      offset: 0,
    },
    totalElements: 0,
  },
  absenceMonitoringFiltersSchema: null,
  reportToSave: null,
  viewState: {
    filtersExpanded: false,
    chartReviewMeetingStatusExpanded: false,
    chartEmployeesPerLevelExpanded: true,
    reportTableExpanded: true,
  },
};

export const setLoading = (state: AbsenceMonitoringState) => () => startRequest(state);

export const onGetReportingAbsenceMonitoringDefaultReportsSuccess = (
  state: AbsenceMonitoringState,
) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    error: null,
    defaultReports: payload,
  };
};

export const onGetReportingAbsenceMonitoringDefaultReportsError = (
  state: AbsenceMonitoringState,
) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    error: payload,
  };
};

export const onGetReportingAbsenceMonitoringReportSuccess = (state: AbsenceMonitoringState) => ({
  payload,
}: Object) => {
  return {
    ...finishRequest(state),
    error: null,
    reportData: payload,
  };
};

export const onGetReportingAbsenceMonitoringReportError = (state: AbsenceMonitoringState) => ({
  payload,
}: Object) => {
  return {
    ...finishRequest(state),
    error: payload,
  };
};

export const onGetReportingAbsenceMonitoringReport = (state: AbsenceMonitoringState) => ({
  payload,
}: Object) => {
  return {
    ...startRequest(state),
    error: null,
    currentReport: payload,
  };
};

export const onGetReportingAbsenceMonitoringChartsSuccess = (state: AbsenceMonitoringState) => ({
  payload,
}: Object) => {
  return {
    ...finishRequest(state),
    error: null,
    currentCharts: payload,
  };
};

export const onGetReportingAbsenceMonitoringChartsError = (state: AbsenceMonitoringState) => ({
  payload,
}: Object) => {
  return {
    ...finishRequest(state),
    error: payload,
  };
};

export const onGetReportingAbsenceMonitoringMySavedReportsSuccess = (
  state: AbsenceMonitoringState,
) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    error: null,
    mySavedReports: payload,
  };
};

export const onGetReportingAbsenceMonitoringMySavedReportsError = (
  state: AbsenceMonitoringState,
) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    error: payload,
  };
};

export const onGetAbsenceMonitoringFiltersSchemaSuccess = (state: AbsenceMonitoringState) => ({
  payload,
}: Object) => {
  return {
    ...finishRequest(state),
    error: null,
    absenceMonitoringFiltersSchema: payload,
  };
};

export const onGetAbsenceMonitoringFiltersSchemaError = (state: AbsenceMonitoringState) => ({
  payload,
}: Object) => {
  return {
    ...finishRequest(state),
    error: payload,
  };
};

export const onSetAbsenceMonitoringReportToSave = (state: AbsenceMonitoringState) => ({
  payload,
}: Object) => {
  return {
    ...state,
    reportToSave: payload,
  };
};

export const onSetViewState = (state: AbsenceMonitoringState) => ({ payload }: Object) => {
  return {
    ...state,
    viewState: payload,
  };
};

export const onResetViewState = (state: AbsenceMonitoringState) => () => {
  return {
    ...state,
    viewState: initialViewState,
  };
};

export const onExportAbsenceMonitoringReport = (state: AbsenceMonitoringState) => () => {
  return {
    ...state,
    reportGenerateInProgress: ++state.reportGenerateInProgress,
  };
};

export const onExportAbsenceMonitoringReportLoaded = (state: AbsenceMonitoringState) => () => {
  return {
    ...state,
    reportGenerateInProgress:
      state.reportGenerateInProgress > 0 ? --state.reportGenerateInProgress : 0,
  };
};

export default (
  state: AbsenceMonitoringState = initialState,
  action: Object,
): AbsenceMonitoringState => {
  const act = {
    [ExportAbsenceMonitoringReport]: onExportAbsenceMonitoringReport,
    [ExportAbsenceMonitoringReportSuccess]: onExportAbsenceMonitoringReportLoaded,
    [ExportAbsenceMonitoringReportError]: onExportAbsenceMonitoringReportLoaded,
    [GetReportingAbsenceMonitoringDefaultReports]: setLoading,
    [GetReportingAbsenceMonitoringDefaultReports]: setLoading,
    [GetReportingAbsenceMonitoringDefaultReportsSuccess]: onGetReportingAbsenceMonitoringDefaultReportsSuccess,
    [GetReportingAbsenceMonitoringDefaultReportsError]: onGetReportingAbsenceMonitoringDefaultReportsError,
    [GetReportingAbsenceMonitoringReport]: onGetReportingAbsenceMonitoringReport,
    [GetReportingAbsenceMonitoringReportSuccess]: onGetReportingAbsenceMonitoringReportSuccess,
    [GetReportingAbsenceMonitoringReportError]: onGetReportingAbsenceMonitoringReportError,
    [GetReportingAbsenceMonitoringMyReports]: setLoading,
    [GetReportingAbsenceMonitoringMyReportsSuccess]: onGetReportingAbsenceMonitoringMySavedReportsSuccess,
    [GetReportingAbsenceMonitoringMyReportsError]: onGetReportingAbsenceMonitoringMySavedReportsError,
    [GetReportingAbsenceMonitoringChartsSuccess]: onGetReportingAbsenceMonitoringChartsSuccess,
    [GetReportingAbsenceMonitoringChartsError]: onGetReportingAbsenceMonitoringChartsError,
    [GetReportingAbsenceMonitoringCharts]: setLoading,
    [GetAbsenceMonitoringFiltersSchema]: setLoading,
    [GetAbsenceMonitoringFiltersSchemaSuccess]: onGetAbsenceMonitoringFiltersSchemaSuccess,
    [GetAbsenceMonitoringFiltersSchemaError]: onGetAbsenceMonitoringFiltersSchemaError,
    [SetAbsenceMonitoringReportToSave]: onSetAbsenceMonitoringReportToSave,
    [SetAbsenceMonitoringReportViewState]: onSetViewState,
    [LOCATION_CHANGE]: onResetViewState,
  };

  if (action && action.type && act && act[action.type]) {
    return act[action.type](cloneObject(state))(action);
  }
  return state;
};
